import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import axios from "axios";
import HouseIcon from "@mui/icons-material/House";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { useNavigate } from "react-router-dom";
import { useAccountsNav } from "../../utils/moduleNav";
import { PawnAccountsSkin } from "./pawnAccountsTheme";

const emptyCostCenter = {
  id: null,
  name: "",
  acc_nos: [],
};

const CostCenterMaster = () => {
  const navigate = useNavigate();
  // Host module: decides the home target and whether the pawn skin applies.
  const { inPawn, goHome } = useAccountsNav();
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;

  // ================= STATES =================
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [filterAccount, setFilterAccount] = useState(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState(emptyCostCenter);
  const [originalCostCenter, setOriginalCostCenter] = useState(null);
  const [refresh, setRefresh] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [costCenterToDelete, setCostCenterToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Helper: parse acc_nos (string or array) into array of numbers
  const parseAccNos = (accNos) => {
    if (!accNos) return [];
    if (Array.isArray(accNos)) return accNos.map((id) => Number(id));
    try {
      const parsed = JSON.parse(accNos);
      if (Array.isArray(parsed)) return parsed.map((id) => Number(id));
      return [];
    } catch (e) {
      return [];
    }
  };

  // ================= FETCH =================
  useEffect(() => {
    if (store_id === null || store_id === undefined) return;

    const fetchAccounts = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/accounts/accounts/${store_id}`;
        const res = await axios.get(url);
        if (res.data?.success) {
          setAccounts(res.data.data || []);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to load accounts",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to load accounts",
          severity: "error",
        });
      }
    };

    const fetchCostCenters = async () => {
      setLoading(true);
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/accounts/cost-centers/${store_id}`;
        const res = await axios.get(url);
        if (res.data?.success) {
          // Ensure every cost center has acc_nos as array of numbers
          const parsedCenters = (res.data.data || []).map((cc) => ({
            ...cc,
            acc_nos: parseAccNos(cc.acc_nos),
          }));
          setCostCenters(parsedCenters);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to load cost centers",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to load cost centers",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
    fetchCostCenters();
  }, [refresh, store_id]);

  // ================= HANDLERS =================
  const handleSelectCostCenter = (cc) => {
    // Ensure acc_nos is an array of numbers
    const accNosArray = parseAccNos(cc.acc_nos);
    setSelectedCostCenter({
      ...cc,
      acc_nos: accNosArray,
    });
    setOriginalCostCenter(cc);
    setIsEditMode(true);
  };

  const handleChangeName = (e) => {
    setSelectedCostCenter({ ...selectedCostCenter, name: e.target.value });
  };

  const handleAccountsChange = (event, newValue) => {
    // newValue is array of selected account objects
    const accountIds = newValue.map((acc) => Number(acc.account_id));
    setSelectedCostCenter({ ...selectedCostCenter, acc_nos: accountIds });
  };

  const resetForm = () => {
    setSelectedCostCenter(emptyCostCenter);
    setOriginalCostCenter(null);
    setIsEditMode(false);
  };

  // ================= CREATE / UPDATE =================
  const handleSubmit = async () => {
    if (!selectedCostCenter.name.trim()) {
      return setSnackbar({
        open: true,
        message: "Name is required",
        severity: "warning",
      });
    }
    if (!selectedCostCenter.acc_nos.length) {
      return setSnackbar({
        open: true,
        message: "At least one account must be selected",
        severity: "warning",
      });
    }

    // Check for changes in edit mode
    if (isEditMode && originalCostCenter) {
      const origAccNos = parseAccNos(originalCostCenter.acc_nos);
      const nameUnchanged =
        selectedCostCenter.name.trim() === originalCostCenter.name.trim();
      const accUnchanged =
        JSON.stringify(selectedCostCenter.acc_nos.sort()) ===
        JSON.stringify(origAccNos.sort());
      if (nameUnchanged && accUnchanged) {
        return setSnackbar({
          open: true,
          message: "No changes detected",
          severity: "warning",
        });
      }
    }

    try {
      let res;
      const payload = {
        name: selectedCostCenter.name,
        store_id,
        acc_nos: selectedCostCenter.acc_nos,
      };

      if (isEditMode) {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/accounts/cost-centers/${selectedCostCenter.id}`;
        res = await axios.put(url, payload);
      } else {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/accounts/cost-centers`;
        res = await axios.post(url, payload);
      }

      if (res.data.success) {
        setSnackbar({
          open: true,
          message:
            res.data.statusMessage || (isEditMode ? "Updated" : "Created"),
          severity: "success",
        });
        resetForm();
        setRefresh(!refresh);
      } else {
        setSnackbar({
          open: true,
          message: res.data.statusMessage || "Operation failed",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.statusMessage ||
          error?.message ||
          "Something went wrong",
        severity: "error",
      });
    }
  };

  // ================= DELETE =================
  const handleDeleteClick = (cc) => {
    setCostCenterToDelete(cc);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!costCenterToDelete) return;
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/accounts/cost-centers/${costCenterToDelete.id}`;
      const res = await axios.delete(url);
      if (res.data.success) {
        setSnackbar({
          open: true,
          message: "Cost center deleted",
          severity: "success",
        });
        setRefresh(!refresh);
        resetForm();
      } else {
        setSnackbar({
          open: true,
          message: "Delete failed",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete",
        severity: "error",
      });
    }
    setDeleteDialogOpen(false);
    setCostCenterToDelete(null);
  };

  // Filter cost centers based on selected filterAccount (compare numbers)
  const filteredCostCenters = costCenters.filter((cc) => {
    if (!filterAccount) return true;
    const filterId = Number(filterAccount.account_id);
    return cc.acc_nos.some((accId) => Number(accId) === filterId);
  });

  // Helper to get account names for display
  const getAccountNames = (accNosArray) => {
    if (!accNosArray || !accNosArray.length) return "No accounts";
    const names = accNosArray
      .map(
        (id) =>
          accounts.find((acc) => Number(acc.account_id) === Number(id))
            ?.account_name,
      )
      .filter(Boolean);
    return names.join(", ");
  };

  // ================= UI =================
  // Held in a const so wrapping it in the pawn skin below doesn't re-indent the
  // whole tree.
  const content = (
    <Box p={2}>
      <Grid container spacing={2}>
        {/* Cost Centers List */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "88vh", overflow: "auto" }}>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
              <Box
                onClick={() => navigate(-1)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 2,
                  bgcolor: "#139fcae8",
                  p: 0.5,
                }}
              >
                <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
              </Box>
              <Box
                onClick={goHome}
                sx={{
                  cursor: "pointer",
                  borderRadius: 2,
                  bgcolor: "#139fcae8",
                  p: 0.5,
                }}
              >
                <HouseIcon sx={{ fill: "#FFF" }} />
              </Box>
              <Typography variant="h6">Existing Cost Centers</Typography>
            </Box>

            <Autocomplete
              options={accounts}
              value={filterAccount}
              onChange={(e, newVal) => setFilterAccount(newVal)}
              getOptionLabel={(opt) => opt?.account_name || ""}
              isOptionEqualToValue={(opt, val) =>
                Number(opt?.account_id) === Number(val?.account_id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Filter by Account (optional)" />
              )}
              sx={{ mb: 2 }}
            />

            {loading ? (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress />
              </Box>
            ) : (
              <List>
                {filteredCostCenters.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      sx={{
                        color: "#a5a5a5",
                        fontStyle: "italic",
                        textAlign: "center",
                      }}
                    >
                      No cost centers found
                    </ListItemText>
                  </ListItem>
                ) : (
                  filteredCostCenters.map((cc) => (
                    <ListItem
                      key={cc.id}
                      secondaryAction={
                        <>
                          <IconButton
                            onClick={() => handleSelectCostCenter(cc)}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(cc)}
                          >
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        </>
                      }
                    >
                      <ListItemText
                        primary={cc.name}
                        secondary={`Accounts: ${getAccountNames(cc.acc_nos)}`}
                        secondaryTypographyProps={{ fontSize: "0.75rem" }}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Cost Center Form */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">
              {isEditMode ? "Edit" : "Create"} Cost Center
            </Typography>
            <Divider sx={{ my: 1 }} />

            <TextField
              fullWidth
              label="Cost Center Name *"
              value={selectedCostCenter.name}
              onChange={handleChangeName}
              sx={{ mt: 2 }}
            />

            {/* Multi-select for accounts */}
            <Autocomplete
              multiple
              options={accounts}
              value={accounts.filter((acc) =>
                selectedCostCenter.acc_nos.some(
                  (id) => Number(id) === Number(acc.account_id),
                ),
              )}
              onChange={handleAccountsChange}
              getOptionLabel={(opt) => opt.account_name}
              isOptionEqualToValue={(opt, val) =>
                Number(opt.account_id) === Number(val.account_id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Accounts *"
                  placeholder="Add accounts"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.account_name}
                    {...getTagProps({ index })}
                  />
                ))
              }
              sx={{ mt: 2 }}
            />

            <Box mt={2} display="flex" gap={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleSubmit}
              >
                {isEditMode ? "Update" : "Create"}
              </Button>
              {isEditMode && (
                <Button variant="outlined" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Cost Center</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{costCenterToDelete?.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  return <PawnAccountsSkin active={inPawn}>{content}</PawnAccountsSkin>;
};

export default CostCenterMaster;
